'use client';
import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { User, Session } from '@supabase/supabase-js';

interface UserProfile {
  id: string
  name?: string
  email?: string
  permission_level?: string
  [key: string]: any
}

interface AuthContextType {
  user: User | null
  userProfile: UserProfile | null
  loading: boolean
  profileLoading: boolean
  signIn: (email: string, password: string) => Promise<any>
  signOut: () => Promise<any>
  updateProfile: (updates: Partial<UserProfile>) => Promise<any>
  resetPassword: (email: string) => Promise<any>
  updatePassword: (newPassword: string) => Promise<any>
  isAuthenticated: boolean
  isAdmin: boolean
}

const AuthContext = createContext<AuthContextType | null>(null)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within AuthProvider')
  return context
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)

  // Computed value for admin status
  const isAdmin = userProfile?.permission_level === 'admin'

  const profileOperations = {
    async load(userId: string) {
      if (!userId) return
      setProfileLoading(true)
      try {
        // Use maybeSingle() instead of single() to avoid PGRST116 error
        const { data, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', userId)
          .maybeSingle()
        
        if (error) {
          console.error('❌ Auth Context: Profile query error', error)
          setProfileLoading(false)
          return
        }

        // If profile doesn't exist, create it
        if (!data) {
          console.log('⚠️ Auth Context: Profile not found, creating new profile')
          
          // Get user details from auth
          const { data: { user } } = await supabase.auth.getUser()
          
          if (user) {
            const newProfile = {
              id: userId,
              email: user.email || '',full_name: user.user_metadata?.full_name || user.email?.split('@')[0] || '',permission_level: user.user_metadata?.permission_level || 'member',profile_image_url: user.user_metadata?.avatar_url || ''
            }

            const { data: createdProfile, error: createError } = await supabase
              .from('user_profiles')
              .insert([newProfile])
              .select()
              .single()

            if (createError) {
              console.error('❌ Auth Context: Failed to create profile', createError)
            } else if (createdProfile) {
              setUserProfile(createdProfile)
              console.log('✅ Auth Context: Profile created successfully', {
                userId: createdProfile.id,
                email: createdProfile.email,
                permission_level: createdProfile.permission_level
              })
            }
          }
        } else {
          // Profile exists, use it
          setUserProfile(data)
          console.log('🔐 Auth Context: Profile loaded', {
            userId: data.id,
            email: data.email,
            permission_level: data.permission_level,
            isAdmin: data.permission_level === 'admin'
          })
        }
      } catch (error) {
        console.error('❌ Auth Context: Profile load exception', error)
      } finally {
        setProfileLoading(false)
      }
    },
    clear() {
      setUserProfile(null)
      setProfileLoading(false)
      console.log('🔓 Auth Context: Profile cleared')
    },
  }

  const authStateHandlers = {
    onChange: (_event: string | null, session: Session | null) => {
      setUser(session?.user ?? null)
      setLoading(false)
      
      console.log('🔄 Auth Context: State changed', {
        hasSession: !!session,
        userId: session?.user?.id,
        email: session?.user?.email
      })
      
      if (session?.user) {
        profileOperations.load(session.user.id)
      } else {
        profileOperations.clear()
      }
    },
  }

  useEffect(() => {
    console.log('🚀 Auth Context: Initializing')
    
    supabase.auth.getSession().then(({ data: { session } }) => {
      authStateHandlers.onChange(null, session)
    })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(authStateHandlers.onChange)

    return () => {
      console.log('🛑 Auth Context: Cleaning up')
      subscription?.unsubscribe()
    }
  }, [])

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password })
      return { data, error }
    } catch {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut()
      if (!error) {
        setUser(null)
        profileOperations.clear()
      }
      return { error }
    } catch {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user) return { error: { message: 'No user logged in' } }
    try {
      const { data, error } = await supabase
        .from('user_profiles').update(updates).eq('id', user.id)
        .select()
        .single()
      if (!error) setUserProfile(data)
      return { data, error }
    } catch {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const resetPassword = async (email: string) => {
    try {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      return { data, error };
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  };

  const updatePassword = async (newPassword: string) => {
    try {
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword,
      });
      return { data, error };
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  };

  const value: AuthContextType = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signOut,
    updateProfile,
    resetPassword,
    updatePassword,
    isAuthenticated: !!user,
    isAdmin,
  }

  return <AuthContext.Provider data-component-id="src/contexts/AuthContext.tsx:224:9" data-component-path="src/contexts/AuthContext.tsx" data-component-line="224" data-component-end-line="224" data-component-file="AuthContext.tsx" data-component-name="AuthContext.Provider" data-component-content="%7B%22elementName%22%3A%22AuthContext.Provider%22%2C%22value%22%3A%22%5Bvar%3Avalue%5D%22%7D" value={value}>{children}</AuthContext.Provider>
}
